import React, { useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import classNames from "classnames";
import ReactPlayer from "react-player";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";

import { WEBPAGE_TITLE } from "@utils/Constants";
import { FormatWebsite } from "@helpers/Formatters";
import { useCompanies } from "@hooks/useCompany";

import styles from "./CompanyDetailsPage.module.scss";

const BRAND = {
    green: "#009448",
    teal: "#1391BC",
    gold: "#E2B93B",
    terra: "#C54E3F",
};

const ESG_COLORS = [
    { color: "#C54E3F", bg: "#fef2f0", labelKey: "policies_category_env" },
    { color: "#E2B93B", bg: "#fdf8e6", labelKey: "policies_category_soc" },
    { color: "#1391BC", bg: "#e8f6fb", labelKey: "policies_category_gov" },
];

const GENDER_PALETTE = {
    women: { color: "#FAB381", labelKey: "gender_women" },
    men: { color: "#FD974F", labelKey: "gender_men" },
    kvar: { color: "#E56810", labelKey: "gender_kvar" },
    other: { color: "#BC5003", labelKey: "gender_other" },
};

function parseCategoryValue(val) {
    const parts = val.split(" ");
    const completed = parseInt(parts[0], 10) || 0;
    const total = parseInt(parts[parts.length - 1], 10) || 0;
    return { completed, total, pct: total > 0 ? (completed / total) * 100 : 0 };
}

function getZone(val, locale) {
    const labels = {
        is: [
            "Fyrstu skref",
            "Á réttu leið",
            "Á réttu leið",
            "Framúrskarandi",
            "Framúrskarandi",
        ],
        en: [
            "First steps",
            "Emerging",
            "Making progress",
            "On track",
            "Outstanding",
        ],
    };
    const colors = ["#b0967a", "#c4a87a", "#b8a472", "#7aab8a", "#5a9e6f"];
    const idx =
        val >= 80 ? 4 : val >= 60 ? 3 : val >= 40 ? 2 : val >= 20 ? 1 : 0;
    return { label: (labels[locale] || labels.is)[idx], color: colors[idx] };
}

function calcOverallScore(greenSteps) {
    if (!greenSteps || greenSteps.length === 0) return 0;
    const values = greenSteps
        .slice(0, 3)
        .map((c) => c.total_category_green_steps || 0);
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function formatJoinedDate(dateStr, t, locale) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const month = t(`months_short_${d.getMonth() + 1}`);
    return locale === "en"
        ? `${month} ${d.getFullYear()}`
        : `${month}. ${d.getFullYear()}`;
}

function parsePolicyComment(commentStr) {
    if (!commentStr) return null;
    try {
        const arr = JSON.parse(commentStr);
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
    } catch {
        return null;
    }
}

function formatPolicyDate(dateStr) {
    if (!dateStr) return null;
    const [dd, mm, yyyy] = dateStr.split("-");
    if (!dd || !mm || !yyyy) return dateStr;
    return `${dd}.${mm}.${yyyy}`;
}

function sanitizeUrl(value) {
    if (!value || typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const firstToken = trimmed.split(/\s+/)[0];

    let candidate = firstToken;
    if (candidate.startsWith("www.")) candidate = `https://${candidate}`;

    if (!/^https?:\/\//i.test(candidate)) return null;

    try {
        const u = new URL(candidate);
        if (!u.hostname || !u.hostname.includes(".")) return null;
        return u.toString();
    } catch {
        return null;
    }
}

function parseStaffData(staff) {
    if (!staff?.[0]) return null;
    try {
        const parsed = JSON.parse(staff[0]);

        const employeeKeys = ["women", "men", "kvar", "other"];
        const employeeFields = {
            women: "woman",
            men: "men",
            kvar: "kvar",
            other: "other",
        };
        const employees = employeeKeys
            .map((k) => ({
                key: k,
                value: parseInt(parsed[employeeFields[k]] || 0, 10),
            }))
            .filter((seg) => seg.value > 0);

        const managerKeys = ["women", "men", "kvar", "other"];
        const managerFields = {
            women: "manager_woman",
            men: "manager_men",
            kvar: "manager_kvar",
            other: "manager_other",
        };
        const managers = managerKeys
            .map((k) => ({
                key: k,
                value: parseInt(parsed[managerFields[k]] || 0, 10),
            }))
            .filter((seg) => seg.value > 0);

        const employeeTotal =
            parseInt(parsed.employees, 10) ||
            employees.reduce((s, e) => s + e.value, 0);
        const managerTotal =
            parseInt(parsed.total, 10) ||
            managers.reduce((s, m) => s + m.value, 0);

        return {
            employees,
            employeeTotal,
            managers,
            managerTotal,
        };
    } catch {
        return null;
    }
}

export default function CompanyDetailsPage({ company }) {
    const t = useTranslations("CustomersPage");
    const { locale, asPath } = useRouter();

    const overallScore = useMemo(
        () => calcOverallScore(company?.new_green_steps),
        [company?.new_green_steps],
    );

    const staffData = useMemo(
        () => parseStaffData(company?.staff),
        [company?.staff],
    );

    const { completedActions, totalActions } = useMemo(() => {
        let done = 0,
            all = 0;
        company?.new_green_steps?.forEach((item) => {
            Object.values(item.categories || {}).forEach((cat) => {
                const p = parseCategoryValue(cat);
                done += p.completed;
                all += p.total;
            });
        });
        return { completedActions: done, totalActions: all };
    }, [company?.new_green_steps]);

    const policiesCount = company?.policies?.length || 0;
    const joinedDate = formatJoinedDate(company?.created_at, t, locale);

    const shareUrl = useMemo(() => {
        if (typeof window !== "undefined") return window.location.href;
        return `https://laufid.is${asPath || ""}`;
    }, [asPath]);

    return (
        <div className={styles.page}>
            <Head>
                <title key="site_title">
                    {company?.name + " - " + WEBPAGE_TITLE}
                </title>
                <meta
                    property="og:title"
                    content={company?.name}
                    key="og:title"
                />
            </Head>

            <HeroSection
                company={company}
                overallScore={overallScore}
                joinedDate={joinedDate}
                shareUrl={shareUrl}
                t={t}
                locale={locale}
            />

            {company?.new_green_steps?.length > 0 && (
                <JourneyTimelineSection
                    joinedDate={joinedDate}
                    completedActions={completedActions}
                    totalActions={totalActions}
                    policiesCount={policiesCount}
                    overallScore={overallScore}
                    t={t}
                />
            )}

            {company?.new_green_steps?.length > 0 && (
                <ChangeCapacityPanel
                    greenSteps={company.new_green_steps}
                    locale={locale}
                    t={t}
                />
            )}

            {staffData &&
                (staffData.employeeTotal > 0 || staffData.managerTotal > 0) && (
                    <PeopleCard staffData={staffData} t={t} />
                )}

            {company?.policies?.length > 0 && (
                <PoliciesSection
                    policies={company.policies}
                    t={t}
                    locale={locale}
                />
            )}

            {company?.stories?.length > 0 && (
                <section className={styles.storiesSection}>
                    <h2 className={styles.sectionTitle}>{t("stories")}</h2>
                    <div className={styles.storiesGrid}>
                        {company.stories.map((story) => (
                            <div className={styles.storyCard} key={story.id}>
                                <div className={styles.storyVideo}>
                                    <ReactPlayer
                                        width="100%"
                                        height="100%"
                                        url={story.link}
                                        controls
                                        playsinline
                                        config={{
                                            youtube: {
                                                playerVars: { showinfo: 0 },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <RelatedCompaniesSection currentCompanyId={company?.id} t={t} />
        </div>
    );
}

function HeroSection({
    company,
    overallScore,
    joinedDate,
    shareUrl,
    t,
    locale,
}) {
    const zone = overallScore > 0 ? getZone(overallScore, locale) : null;

    return (
        <section className={styles.hero}>
            {company?.cover_photo && (
                <div className={styles.heroBanner}>
                    <img src={company.cover_photo} alt={company.name} />
                    <div className={styles.heroBannerOverlay} />
                </div>
            )}
            <div
                className={classNames(styles.heroContent, {
                    [styles.noBanner]: !company?.cover_photo,
                })}
            >
                <div className={styles.heroLogo}>
                    {company?.logo ? (
                        <img src={company.logo} alt={company.name + " logo"} />
                    ) : (
                        <div className={styles.heroLogoPlaceholder}>
                            {company?.name?.[0]}
                        </div>
                    )}
                </div>
                <div className={styles.heroInfo}>
                    <h1 className={styles.heroName}>{company?.name}</h1>
                    {company?.description && (
                        <p className={styles.heroDesc}>{company.description}</p>
                    )}
                    <div className={styles.heroMeta}>
                        {company?.website && (
                            <a
                                href={company.website}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.metaLink}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                                </svg>
                                {FormatWebsite(company.website)}
                            </a>
                        )}
                        {company?.contact_name && (
                            <a
                                href={
                                    company?.contact_email
                                        ? `mailto:${company.contact_email}`
                                        : undefined
                                }
                                className={styles.metaLink}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                                {company.contact_name}
                            </a>
                        )}
                        {joinedDate && (
                            <span className={styles.heroMetaChip}>
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <rect
                                        x="3"
                                        y="4"
                                        width="18"
                                        height="18"
                                        rx="2"
                                        ry="2"
                                    />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                {t("hero_joined_label")} {joinedDate}
                            </span>
                        )}
                    </div>
                </div>

                {overallScore > 0 && zone && (
                    <div
                        className={styles.heroScore}
                        style={{ "--score-color": zone.color }}
                    >
                        <span className={styles.heroScoreLabel}>
                            {t("hero_overall_score_label")}
                        </span>
                        <span className={styles.heroScoreValue}>
                            {overallScore}%
                        </span>
                        <span className={styles.heroScoreBadge}>
                            <span className={styles.heroScoreDot} />
                            {zone.label}
                        </span>
                    </div>
                )}
            </div>
            <div className={styles.heroLine} />
        </section>
    );
}

function ShareButtons({ shareUrl, title, t }) {
    const [copied, setCopied] = useState(false);

    const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    return (
        <div className={styles.shareRow}>
            <span className={styles.shareLabel}>{t("share_title")}:</span>
            <a
                href={linkedin}
                target="_blank"
                rel="noreferrer"
                className={styles.shareBtn}
                aria-label={t("share_linkedin")}
                title={t("share_linkedin")}
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43c-1.14 0-2.06-.92-2.06-2.06 0-1.14.92-2.06 2.06-2.06s2.06.92 2.06 2.06c0 1.14-.92 2.06-2.06 2.06zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
                </svg>
            </a>
            <a
                href={facebook}
                target="_blank"
                rel="noreferrer"
                className={styles.shareBtn}
                aria-label={t("share_facebook")}
                title={t("share_facebook")}
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z" />
                </svg>
            </a>
            <button
                type="button"
                className={classNames(styles.shareBtn, styles.shareCopy, {
                    [styles.shareCopied]: copied,
                })}
                onClick={handleCopy}
                aria-label={t("share_copy")}
                title={t("share_copy")}
            >
                {copied ? (
                    <>
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                        >
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className={styles.shareCopiedText}>
                            {t("share_copied")}
                        </span>
                    </>
                ) : (
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                    </svg>
                )}
            </button>
        </div>
    );
}

function JourneyTimelineSection({
    joinedDate,
    completedActions,
    totalActions,
    policiesCount,
    overallScore,
    t,
}) {
    const milestones = [
        joinedDate && {
            label: t("journey_joined"),
            value: joinedDate,
            color: BRAND.green,
            icon: (
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            ),
        },
        {
            label: t("journey_actions"),
            value: `${completedActions} / ${totalActions}`,
            color: BRAND.teal,
            icon: (
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            ),
        },
        policiesCount > 0 && {
            label: t("journey_policies"),
            value: policiesCount,
            color: BRAND.gold,
            icon: (
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                </svg>
            ),
        },
        {
            label: t("journey_score"),
            value: `${overallScore}%`,
            color: BRAND.terra,
            icon: (
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M12 2l2.4 7.2h7.6l-6.2 4.5 2.4 7.3L12 16.5l-6.2 4.5 2.4-7.3L2 9.2h7.6z" />
                </svg>
            ),
        },
    ].filter(Boolean);

    return (
        <section className={styles.journeySection}>
            <div className={styles.journeyHeader}>
                <h2 className={styles.journeyTitle}>{t("journey_title")}</h2>
                <p className={styles.journeySubtitle}>
                    {t("journey_subtitle")}
                </p>
            </div>
            <div
                className={styles.journeyGrid}
                style={{
                    gridTemplateColumns: `repeat(${milestones.length}, 1fr)`,
                }}
            >
                {milestones.map((m, i) => (
                    <div
                        key={i}
                        className={styles.journeyMilestone}
                        style={{
                            "--ms-color": m.color,
                            animationDelay: `${i * 0.08}s`,
                        }}
                    >
                        <div
                            className={styles.journeyIcon}
                            style={{
                                color: m.color,
                                background: `${m.color}14`,
                            }}
                        >
                            {m.icon}
                        </div>
                        <div className={styles.journeyBody}>
                            <span className={styles.journeyLabel}>
                                {m.label}
                            </span>
                            <span className={styles.journeyValue}>
                                {m.value}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function PoliciesSection({ policies, t, locale }) {
    const filters = [
        { id: "all", label: t("policies_filter_all"), color: "#64748b" },
        {
            id: 1,
            label: t("policies_category_env"),
            color: ESG_COLORS[0].color,
        },
        {
            id: 2,
            label: t("policies_category_soc"),
            color: ESG_COLORS[1].color,
        },
        {
            id: 3,
            label: t("policies_category_gov"),
            color: ESG_COLORS[2].color,
        },
    ];
    const [activeFilter, setActiveFilter] = useState("all");

    const counts = useMemo(() => {
        const c = { 1: 0, 2: 0, 3: 0 };
        policies.forEach((p) => {
            if (c[p.main_category] != null) c[p.main_category]++;
        });
        return c;
    }, [policies]);

    const filtered =
        activeFilter === "all"
            ? policies
            : policies.filter((p) => p.main_category === activeFilter);

    return (
        <section className={styles.policiesSection}>
            <div className={styles.policiesHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>
                        {t("policies_title")}
                    </h2>
                    <p className={styles.sectionSub}>
                        {t("policies_subtitle")}
                    </p>
                </div>
                <div className={styles.policiesFilters}>
                    {filters.map((f) => {
                        const count =
                            f.id === "all"
                                ? policies.length
                                : counts[f.id] || 0;
                        if (f.id !== "all" && count === 0) return null;
                        const isActive = activeFilter === f.id;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                className={classNames(styles.policyFilter, {
                                    [styles.policyFilterActive]: isActive,
                                })}
                                style={{ "--filter-color": f.color }}
                                onClick={() => setActiveFilter(f.id)}
                            >
                                <span className={styles.policyFilterDot} />
                                {f.label}
                                <span className={styles.policyFilterCount}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={styles.policiesGrid}>
                {filtered.map((policy, i) => (
                    <PolicyCard
                        key={policy.id}
                        policy={policy}
                        t={t}
                        locale={locale}
                        idx={i}
                    />
                ))}
            </div>
        </section>
    );
}

function PolicyCard({ policy, t, locale, idx }) {
    const categoryIdx = (policy.main_category || 1) - 1;
    const color = ESG_COLORS[categoryIdx]?.color || BRAND.green;
    const bg = ESG_COLORS[categoryIdx]?.bg || "#f0fdf4";
    const categoryLabel = t(
        ESG_COLORS[categoryIdx]?.labelKey || "policies_category_env",
    );

    const name =
        locale === "en"
            ? policy.policy_name_en || policy.policy_name
            : policy.policy_name;
    const title =
        locale === "en" ? policy.title_en || policy.title : policy.title;

    const entry = policy.new_company_green_steps?.[0];
    const pdfUrl = sanitizeUrl(entry?.pdf);
    const pdfName = entry?.pdf_name;
    const linkUrl = sanitizeUrl(entry?.link);
    const commentData = parsePolicyComment(entry?.comment);
    const approvedDate = formatPolicyDate(commentData?.date);

    return (
        <article
            className={styles.policyCard}
            style={{
                "--policy-color": color,
                "--policy-bg": bg,
                animationDelay: `${idx * 0.05}s`,
            }}
        >
            <div className={styles.policyCardHeader}>
                <span className={styles.policyCategoryBadge}>
                    <span className={styles.policyCategoryDot} />
                    {categoryLabel}
                </span>
                {approvedDate && (
                    <span className={styles.policyApprovedChip}>
                        {t("policies_approved")} {approvedDate}
                    </span>
                )}
            </div>

            <h3 className={styles.policyName}>{name}</h3>
            <p className={styles.policyTitle}>{title}</p>

            <div className={styles.policyCardFooter}>
                {pdfUrl ? (
                    <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.policyAction}
                        download={pdfName || undefined}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {t("policies_download_pdf")}
                    </a>
                ) : linkUrl ? (
                    <a
                        href={linkUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.policyAction}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                        </svg>
                        {t("policies_view_link")}
                    </a>
                ) : (
                    <span className={styles.policyActionDisabled}>
                        {t("policies_no_document")}
                    </span>
                )}
            </div>
        </article>
    );
}

function ChangeCapacityPanel({ greenSteps, locale, t }) {
    const cats = greenSteps.slice(0, 3);

    const totalCompleted = cats.reduce((sum, cat) => {
        return (
            sum +
            Object.values(cat.categories || {}).reduce(
                (s, v) => s + parseCategoryValue(v).completed,
                0,
            )
        );
    }, 0);
    const totalAll = cats.reduce((sum, cat) => {
        return (
            sum +
            Object.values(cat.categories || {}).reduce(
                (s, v) => s + parseCategoryValue(v).total,
                0,
            )
        );
    }, 0);
    const totalPct =
        totalAll > 0 ? Math.round((totalCompleted / totalAll) * 100) : 0;
    const zone = getZone(totalPct, locale);

    const circumference = 2 * Math.PI * 36;
    const offset = circumference - (totalPct / 100) * circumference;

    return (
        <section className={styles.ccPanel}>
            <div className={styles.ccHeader}>
                <div className={styles.ccHeaderLeft}>
                    <div className={styles.ccRing}>
                        <svg width="80" height="80" viewBox="0 0 80 80">
                            <circle
                                cx="40"
                                cy="40"
                                r="36"
                                fill="none"
                                stroke="#e8f5ee"
                                strokeWidth="5"
                            />
                            <circle
                                cx="40"
                                cy="40"
                                r="36"
                                fill="none"
                                stroke={BRAND.green}
                                strokeWidth="5"
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                                strokeLinecap="round"
                                transform="rotate(-90 40 40)"
                                className={styles.circProgressRing}
                            />
                            <text
                                x="40"
                                y="40"
                                textAnchor="middle"
                                dy=".35em"
                                fontSize="18"
                                fontWeight="700"
                                fill={BRAND.green}
                            >
                                {totalPct}%
                            </text>
                        </svg>
                    </div>
                    <div className={styles.ccHeaderText}>
                        <h2 className={styles.ccTitle}>{t("esg_overview")}</h2>
                        <p className={styles.ccSubtitle}>
                            {t("under_each_leaf")}
                        </p>
                    </div>
                </div>
                <div className={styles.ccHeaderRight}>
                    <div className={styles.ccScorePill}>
                        <span className={styles.ccScoreNum}>
                            {totalCompleted}
                        </span>
                        <span className={styles.ccScoreSep}>/</span>
                        <span className={styles.ccScoreTotal}>{totalAll}</span>
                    </div>
                    <span
                        className={styles.ccZoneBadge}
                        style={{
                            background: `${zone.color}14`,
                            color: zone.color,
                        }}
                    >
                        <span
                            className={styles.gaugeZoneDot}
                            style={{ background: zone.color }}
                        />
                        {zone.label}
                    </span>
                </div>
            </div>

            <div className={styles.ccColumns}>
                {cats.map((cat, idx) => {
                    const color = ESG_COLORS[idx]?.color || BRAND.green;
                    const name = ESG_COLORS[idx]?.labelKey
                        ? t(ESG_COLORS[idx].labelKey)
                        : cat.name;
                    const pct = cat.total_category_green_steps || 0;
                    const categories = Object.entries(cat.categories || {});

                    return (
                        <div
                            key={idx}
                            className={styles.ccColumn}
                            style={{
                                "--cc-color": color,
                                animationDelay: `${idx * 0.08}s`,
                            }}
                        >
                            <div className={styles.ccColHeader}>
                                <span
                                    className={styles.ccColAccent}
                                    style={{ background: color }}
                                />
                                <span className={styles.ccColName}>{name}</span>
                                <span
                                    className={styles.ccColPct}
                                    style={{ color }}
                                >
                                    {pct}%
                                </span>
                            </div>
                            <div className={styles.ccSubcats}>
                                {categories.map(([catName, catVal]) => {
                                    const {
                                        completed,
                                        total,
                                        pct: barPct,
                                    } = parseCategoryValue(catVal);
                                    return (
                                        <div
                                            key={catName}
                                            className={styles.ccSubcatRow}
                                        >
                                            <div
                                                className={styles.ccSubcatLabel}
                                            >
                                                <span
                                                    className={
                                                        styles.ccSubcatName
                                                    }
                                                >
                                                    {catName}
                                                </span>
                                                <span
                                                    className={
                                                        styles.ccSubcatCount
                                                    }
                                                    style={{ color }}
                                                >
                                                    {completed}/{total}
                                                </span>
                                            </div>
                                            <div
                                                className={styles.ccSubcatTrack}
                                            >
                                                <div
                                                    className={
                                                        styles.ccSubcatFill
                                                    }
                                                    style={{
                                                        width: `${barPct}%`,
                                                        background: color,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function PeopleCard({ staffData, t }) {
    const { employees, employeeTotal, managers, managerTotal } = staffData;

    return (
        <section className={styles.peopleCard}>
            <h2 className={styles.sectionTitle}>{t("people_card_title")}</h2>

            <div className={styles.peopleGrid}>
                <div className={styles.peopleBlock}>
                    <div className={styles.peopleDonutWrap}>
                        <DonutChart
                            segments={employees}
                            total={employeeTotal}
                        />
                        <div className={styles.peopleDonutCenter}>
                            <span className={styles.peopleDonutValue}>
                                {employeeTotal}
                            </span>
                            <span className={styles.peopleDonutLabel}>
                                {t("number_of_employees")}
                            </span>
                        </div>
                    </div>
                    <div className={styles.peopleLegend}>
                        {employees.map((seg) => {
                            const palette = GENDER_PALETTE[seg.key];
                            const pct =
                                employeeTotal > 0
                                    ? Math.round(
                                          (seg.value / employeeTotal) * 100,
                                      )
                                    : 0;
                            return (
                                <div
                                    key={seg.key}
                                    className={styles.peopleLegendItem}
                                >
                                    <span
                                        className={styles.peopleLegendDot}
                                        style={{ background: palette.color }}
                                    />
                                    <span className={styles.peopleLegendLabel}>
                                        {t(palette.labelKey)}
                                    </span>
                                    <span className={styles.peopleLegendValue}>
                                        {seg.value}
                                    </span>
                                    <span className={styles.peopleLegendPct}>
                                        {pct}%
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {managerTotal > 0 && managers.length > 0 && (
                    <div className={styles.peopleBlock}>
                        <div className={styles.peopleMgmtHeader}>
                            <h3 className={styles.peopleMgmtTitle}>
                                {t("people_management_title")}
                            </h3>
                            <span className={styles.peopleMgmtCount}>
                                {t("people_management_count", {
                                    count: managerTotal,
                                })}
                            </span>
                        </div>

                        <div className={styles.peopleMgmtBar}>
                            {managers.map((seg) => {
                                const palette = GENDER_PALETTE[seg.key];
                                const widthPct =
                                    (seg.value / managerTotal) * 100;
                                return (
                                    <div
                                        key={seg.key}
                                        className={styles.peopleMgmtSeg}
                                        style={{
                                            width: `${widthPct}%`,
                                            background: palette.color,
                                        }}
                                        title={`${t(palette.labelKey)}: ${seg.value}`}
                                    />
                                );
                            })}
                        </div>

                        <div className={styles.peopleMgmtRows}>
                            {managers.map((seg) => {
                                const palette = GENDER_PALETTE[seg.key];
                                const pct =
                                    managerTotal > 0
                                        ? Math.round(
                                              (seg.value / managerTotal) * 100,
                                          )
                                        : 0;
                                return (
                                    <div
                                        key={seg.key}
                                        className={styles.peopleMgmtRow}
                                    >
                                        <span
                                            className={styles.peopleLegendDot}
                                            style={{
                                                background: palette.color,
                                            }}
                                        />
                                        <span
                                            className={styles.peopleLegendLabel}
                                        >
                                            {t(palette.labelKey)}
                                        </span>
                                        <span
                                            className={styles.peopleLegendValue}
                                        >
                                            {seg.value}
                                        </span>
                                        <span
                                            className={styles.peopleLegendPct}
                                        >
                                            {pct}%
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {managers.length === 2 &&
                            managers[0].value === managers[1].value && (
                                <span className={styles.peopleBalancedBadge}>
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    {t("people_management_balanced")} 50/50
                                </span>
                            )}
                    </div>
                )}
            </div>
        </section>
    );
}

function DonutChart({ segments, total }) {
    if (!segments?.length || !total) return null;
    const radius = 56;
    const circumference = 2 * Math.PI * radius;
    let cumulative = 0;

    return (
        <svg
            width="140"
            height="140"
            viewBox="0 0 140 140"
            className={styles.donutSvg}
        >
            <circle
                cx="70"
                cy="70"
                r={radius}
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="14"
            />
            {segments.map((seg) => {
                const palette = GENDER_PALETTE[seg.key];
                const pct = seg.value / total;
                const dash = pct * circumference;
                const offset = -cumulative * circumference;
                cumulative += pct;
                return (
                    <circle
                        key={seg.key}
                        cx="70"
                        cy="70"
                        r={radius}
                        fill="none"
                        stroke={palette.color}
                        strokeWidth="14"
                        strokeDasharray={`${dash} ${circumference}`}
                        strokeDashoffset={offset}
                        transform="rotate(-90 70 70)"
                        className={styles.donutSeg}
                    />
                );
            })}
        </svg>
    );
}

function RelatedCompaniesSection({ currentCompanyId, t }) {
    const { data } = useCompanies({ limit: 30 }, { revalidateOnFocus: false });

    const related = useMemo(() => {
        const list = data?.[0]?.results || [];
        const eligible = list.filter(
            (c) =>
                c.id !== currentCompanyId &&
                c.logo &&
                c.visible !== false &&
                !c.disabled,
        );
        const offset = currentCompanyId
            ? currentCompanyId % Math.max(1, eligible.length - 2)
            : 0;
        return eligible.slice(offset, offset + 3);
    }, [data, currentCompanyId]);

    if (related.length === 0) return null;

    return (
        <section className={styles.relatedSection}>
            <div className={styles.relatedHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>
                        {t("related_title")}
                    </h2>
                    <p className={styles.sectionSub}>{t("related_subtitle")}</p>
                </div>
                <Link href="/vidskiptavinir" className={styles.relatedViewAll}>
                    {t("related_view_all")}
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                    </svg>
                </Link>
            </div>

            <div className={styles.relatedGrid}>
                {related.map((c, i) => (
                    <Link
                        key={c.id}
                        href={`/vidskiptavinir/${c.name.replaceAll(" ", "+")}`}
                        className={styles.relatedCard}
                        style={{ animationDelay: `${i * 0.05}s` }}
                    >
                        <div className={styles.relatedCardLogo}>
                            <img
                                src={c.logo}
                                alt={`${c.name} logo`}
                                loading="lazy"
                            />
                        </div>
                        <div className={styles.relatedCardBody}>
                            <h3 className={styles.relatedCardName}>{c.name}</h3>
                            {c.website && (
                                <span className={styles.relatedCardWebsite}>
                                    {FormatWebsite(c.website)}
                                </span>
                            )}
                        </div>
                        <svg
                            className={styles.relatedCardArrow}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                        </svg>
                    </Link>
                ))}
            </div>
        </section>
    );
}
